import { FileUploadedRefType } from "@validereinc/common-components";
import { Canvas, Path, XY } from "fabric";
import { create } from "zustand";
import {
  AggregateFunctionType,
  backgroundType,
  directionType,
  measurementsType,
  ValueType,
} from "../routes/ctr-annotation/FabricCTR.helpers";

type AnnotationStoreType = {
  // Measurement data
  measurements: Record<`${directionType}_${measurementsType}`, ValueType>;
  setMeasurements: (
    measurements: Record<`${directionType}_${measurementsType}`, ValueType>
  ) => void;
  updateMeasurement: (
    key: `${directionType}_${measurementsType}`,
    value: ValueType
  ) => void;

  // Active measurement state
  activeMeasurement:
    | {
        measurement: measurementsType;
        direction: directionType;
        measurementPoints: XY[];
        topTickY: number;
        bottomTickY: number;
        topTickX: number;
        bottomTickX: number;
        topTickValue: number;
        bottomTickValue: number;
        aggregateFunction: AggregateFunctionType;
      }
    | undefined;
  setActiveMeasurement: (
    activeMeasurement: AnnotationStoreType["activeMeasurement"]
  ) => void;
  updateActiveMeasurement: (
    updates: Partial<NonNullable<AnnotationStoreType["activeMeasurement"]>>
  ) => void;
  clearActiveMeasurement: () => void;

  // Step management
  activeStep: number;
  setActiveStep: (step: number) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  resetSteps: () => void;

  // Drawing state
  isDrawingEnabled: boolean;
  setIsDrawingEnabled: (enabled: boolean) => void;

  // Path state
  drawnPath: Path | undefined; // Fabric Path object
  setDrawnPath: (path: Path | undefined) => void;
  clearDrawnPath: () => void;

  // Tick visibility
  tickVisibility: {
    top: boolean;
    bottom: boolean;
  };
  setTickVisibility: (visibility: { top: boolean; bottom: boolean }) => void;
  updateTickVisibility: (position: "top" | "bottom", visible: boolean) => void;

  // Background image
  currentBackgroundImage: backgroundType;
  setCurrentBackgroundImage: (image: backgroundType) => void;
  toggleBackgroundImage: () => void;

  // Canvas resize ratio
  resizeRatio: number | null;
  setResizeRatio: (ratio: number | null) => void;

  // Background images
  imageURLs: Record<backgroundType, string> | null;
  setImageURLs: (imageURLs: Record<backgroundType, string> | null) => void;

  dilutionCorrection: number | null;
  parallaxCorrection: number | null;

  setCorrectionValues: (values: {
    dilutionCorrection: number;
    parallaxCorrection: number;
  }) => void;

  // Loading state
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Saving state
  isSaving: boolean;
  setIsSaving: (saving: boolean) => void;

  // Progress tracking
  progressStep: "generating" | "uploading_images" | "uploading_data" | "done";
  setProgressStep: (
    step: "generating" | "uploading_images" | "uploading_data" | "done"
  ) => void;
  resetProgress: () => void;

  // Submission error tracking
  submissionError:
    | "uploading_images"
    | "uploading_data"
    | "task_already_completed"
    | null;
  setSubmissionError: (
    error:
      | "uploading_images"
      | "uploading_data"
      | "task_already_completed"
      | null
  ) => void;

  // Canvas view state
  fabricCanvas: Canvas | null; // Canvas from fabric.js
  setFabricCanvas: (canvas: Canvas | null) => void;
  finalScreenshots: Record<`${directionType}_${backgroundType}`, string | null>;
  setFinalScreenshot: (
    measurement: `${directionType}_${backgroundType}`,
    screenshot: string | null
  ) => void;
  uploadedScreenshotRefs: Record<
    `${directionType}_${backgroundType}`,
    FileUploadedRefType | null
  >;
  setUploadedScreenshotRef: (
    measurement: `${directionType}_${backgroundType}`,
    ref: FileUploadedRefType | null
  ) => void;
  measurementData: Record<
    `${directionType}_${measurementsType}`,
    {
      measurement: measurementsType;
      direction: directionType;
      measurementPoints: XY[];
      topTickY: number;
      bottomTickY: number;
      topTickX: number;
      bottomTickX: number;
      topTickValue: number;
      bottomTickValue: number;
      aggregateFunction: AggregateFunctionType;
    } | null
  >;
  setMeasurementData: (
    measurement: `${directionType}_${measurementsType}`,
    measurementData: {
      measurement: measurementsType;
      direction: directionType;
      measurementPoints: XY[];
      topTickY: number;
      bottomTickY: number;
      topTickX: number;
      bottomTickX: number;
      topTickValue: number;
      bottomTickValue: number;
      aggregateFunction: AggregateFunctionType;
    } | null
  ) => void;

  // Reset all state
  reset: () => void;
};

const initialMeasurements: Record<
  `${directionType}_${measurementsType}`,
  ValueType
> = {
  left_sediment: undefined,
  left_water: undefined,
  left_interface: undefined,
  right_sediment: undefined,
  right_water: undefined,
  right_interface: undefined,
};

const initialTickVisibility = {
  top: false,
  bottom: false,
};

const initialStoreValues = {
  measurements: initialMeasurements,
  activeMeasurement: undefined,
  activeStep: 1,
  isDrawingEnabled: false,
  drawnPath: undefined,
  tickVisibility: initialTickVisibility,
  currentBackgroundImage: "regular",
  resizeRatio: null,
  imageURLs: null,
  isLoading: false,
  isSaving: false,
  progressStep: "generating",
  dilutionCorrection: null,
  parallaxCorrection: null,
  fabricCanvas: null,
  finalScreenshots: {
    left_regular: null,
    left_uv: null,
    right_regular: null,
    right_uv: null,
  },
  uploadedScreenshotRefs: {
    left_regular: null,
    left_uv: null,
    right_regular: null,
    right_uv: null,
  },
  measurementData: {
    left_water: null,
    left_interface: null,
    left_sediment: null,
    right_sediment: null,
    right_water: null,
    right_interface: null,
  },
  submissionError: null,
} as AnnotationStoreType;

export const useAnnotationStore = create<AnnotationStoreType>((set) => ({
  // Initial state
  ...initialStoreValues,

  // Measurement actions
  setMeasurements: (measurements) => set({ measurements }),
  updateMeasurement: (key, value) =>
    set((state) => ({
      measurements: { ...state.measurements, [key]: value },
    })),

  // Active measurement actions
  setActiveMeasurement: (activeMeasurement) => set({ activeMeasurement }),
  updateActiveMeasurement: (updates) =>
    set((state) => ({
      activeMeasurement: state.activeMeasurement
        ? { ...state.activeMeasurement, ...updates }
        : undefined,
    })),
  clearActiveMeasurement: () => set({ activeMeasurement: undefined }),

  // Step actions
  setActiveStep: (step) => set({ activeStep: step }),
  goToNextStep: () =>
    set((state) => ({ activeStep: Math.min(state.activeStep + 1, 3) })),
  goToPreviousStep: () =>
    set((state) => {
      if (state.activeStep > 1)
        return { activeStep: Math.max(state.activeStep - 1, 1) };
      return state;
    }),
  resetSteps: () => set({ activeStep: 1 }),

  // Drawing actions
  setIsDrawingEnabled: (enabled) => set({ isDrawingEnabled: enabled }),

  // Path actions
  setDrawnPath: (path) => set({ drawnPath: path }),
  clearDrawnPath: () => set({ drawnPath: undefined }),

  // Tick visibility actions
  setTickVisibility: (visibility) => set({ tickVisibility: visibility }),
  updateTickVisibility: (position, visible) =>
    set((state) => ({
      tickVisibility: { ...state.tickVisibility, [position]: visible },
    })),

  // Background image actions
  setCurrentBackgroundImage: (image) => set({ currentBackgroundImage: image }),
  toggleBackgroundImage: () =>
    set((state) => ({
      currentBackgroundImage:
        state.currentBackgroundImage === "regular" ? "uv" : "regular",
    })),

  // Resize ratio actions
  setResizeRatio: (ratio) => set({ resizeRatio: ratio }),

  // Background images actions
  setImageURLs: (imageURLs) => set({ imageURLs }),

  // Loading state actions
  setIsLoading: (loading) => set({ isLoading: loading }),

  // Saving state actions
  setIsSaving: (saving) => set({ isSaving: saving }),

  // Progress tracking actions
  setProgressStep: (step) => set({ progressStep: step }),
  resetProgress: () => set({ progressStep: "generating" }),

  // Submission error actions
  setSubmissionError: (error) => set({ submissionError: error }),

  // Canvas view state actions
  setFabricCanvas: (canvas) => set({ fabricCanvas: canvas }),
  setFinalScreenshot: (measurement, screenshot) =>
    set((state) => ({
      finalScreenshots: {
        ...state.finalScreenshots,
        [measurement]: screenshot,
      },
    })),
  setUploadedScreenshotRef: (measurement, ref) =>
    set((state) => ({
      uploadedScreenshotRefs: {
        ...state.uploadedScreenshotRefs,
        [measurement]: ref,
      },
    })),
  setMeasurementData: (measurement, measurementData) =>
    set((state) => ({
      measurementData: {
        ...state.measurementData,
        [measurement]: measurementData,
      },
    })),

  setCorrectionValues: ({
    parallaxCorrection,
    dilutionCorrection,
  }: {
    dilutionCorrection: number;
    parallaxCorrection: number;
  }) => set({ dilutionCorrection, parallaxCorrection }),

  // Reset all state
  reset: () => set(initialStoreValues),
}));

// Custom hook for computed properties
export const useAnnotationToolProps = () => {
  const { activeStep, activeMeasurement } = useAnnotationStore();

  return {
    canDraw: activeStep === 1,
    canAddTicks: activeStep === 2,
    focusedHalf: activeMeasurement?.direction,
    enabled: !!activeMeasurement,
    aggregateFunction: activeMeasurement?.aggregateFunction,
  };
};
